import {
    Box,
    Button,
    ContextualBanner,
    DatePicker,
    ErrorIcon,
    LinkText,
    Modal,
    Select,
    Upload,
} from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { FileDrop } from "react-file-drop";
import { useTranslation } from "react-i18next";

import style from "components/asset-management-system/upload-custom-report-dialog.scss";
import { CalendarIcon } from "components/icons/CalendarIcon";
import { SUCCESSFUL_ERASURE } from "domain/asset-management/assets";
import { hasHttpStatus } from "domain/fileSharing";
import { assetService } from "services/asset-management/AssetService";
import { fileSharingService, generateChecksumSha256 } from "services/file-sharing/FileSharingService";
import { resolveColor } from "utils/commonFunctions";
import { formatIsoDate, toUtcDateString } from "utils/format";
import { logger } from "utils/logging";
import { useTheme } from "utils/useTheme";

interface Props {
    isOpen: boolean;
    assetUuid: string;
    onClose: () => void;
    assetIdentifier: string;
    onUploadComplete: () => void;
    setUploading: (uploading: boolean) => void;
    uploading: boolean;
}

const ERASURE_STATUS_ITEMS = [
    { value: "SUCCESSFUL", label: "Successful" },
    { value: "FAILED", label: "Failed" },
    { value: "CANCELLED", label: "Cancelled" },
    { value: "DIAGNOSED", label: "Diagnosed" },
    { value: "UNKNOWN", label: "Unknown" },
];

const ASSET_INVENTORY_FOLDER = "Asset-inventory";
const FOLDER_PLACEHOLDER_FILENAME = "__folderPlaceholder";
const POST_UPLOAD_REFRESH_DELAY_MS = 1_500;

function extractServerMessage(error: unknown): string | undefined {
    if (!(error instanceof Error)) {
        return undefined;
    }
    try {
        const response = JSON.parse(error.message);
        const message = response.error?.message ?? response.message;
        return typeof message === "string" && message.trim() !== "" ? message.trim() : undefined;
    } catch {
        return undefined;
    }
}

function resolveErasureDate(selectedDate: Date): Date {
    const now = new Date();
    if (formatIsoDate(selectedDate) === formatIsoDate(now)) {
        return now;
    }
    return new Date(`${formatIsoDate(selectedDate)}T00:00:00Z`);
}

export function UploadCustomReportDialog(props: Props) {
    const { t } = useTranslation();
    const theme = useTheme();

    const fileInputRef = React.useRef<HTMLInputElement>(null);
    const abortControllerRef = React.useRef<AbortController | null>(null);

    const [errorMessage, setErrorMessage] = React.useState<string | undefined>();
    const [erasureStatus, setErasureStatus] = React.useState<string>(SUCCESSFUL_ERASURE);
    const [erasureDate, setErasureDate] = React.useState<Date | undefined>();
    const [selectedFile, setSelectedFile] = React.useState<File | undefined>();
    const datePickerAnchorRef = React.useRef<HTMLDivElement>(null);

    const [openCount, setOpenCount] = React.useState(0);
    const [dateError, setDateError] = React.useState<string | undefined>();

    function getDatePickerContainer(): HTMLElement {
        let element: HTMLElement | null = datePickerAnchorRef.current;
        while (element) {
            if (getComputedStyle(element).position === "fixed") {
                return element;
            }
            element = element.parentElement;
        }
        return document.body;
    }

    React.useEffect(() => {
        if (!props.isOpen) {
            return;
        }
        setOpenCount((prev) => prev + 1);
        return () => {
            abortControllerRef.current?.abort();
            props.setUploading(false);
            setErrorMessage(undefined);
            setErasureStatus(SUCCESSFUL_ERASURE);
            setErasureDate(undefined);
            setSelectedFile(undefined);
            setDateError(undefined);
        };
    }, [props.isOpen]);

    function handleFile(file: File) {
        setSelectedFile(file);
        setErrorMessage(undefined);
    }

    async function handleSubmit() {
        if (selectedFile === undefined) {
            return;
        }
        if (erasureDate === undefined) {
            setDateError(t("AssetManagement.sidePanel.erasureHistory.dateRequired"));
            return;
        }
        setDateError(undefined);

        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        props.setUploading(true);
        setErrorMessage(undefined);
        try {
            const { files } = await fileSharingService.listFiles({
                directoryPath: ASSET_INVENTORY_FOLDER,
                abortController,
            });
            if (files.length === 0) {
                const placeholderBuffer = new Uint8Array([0]).buffer;
                const placeholderChecksum = await generateChecksumSha256(placeholderBuffer);
                const { presigned_url: placeholderUrl } = await fileSharingService.initiateUpload(
                    {
                        filename: `${ASSET_INVENTORY_FOLDER}/${FOLDER_PLACEHOLDER_FILENAME}`,
                        file_size: 1,
                        checksum: placeholderChecksum,
                    },
                    abortController
                );
                await fileSharingService.uploadToS3(placeholderUrl, placeholderBuffer, abortController.signal);
            }

            const buffer = await selectedFile.arrayBuffer();
            const checksum = await generateChecksumSha256(buffer);
            const { uuid, presigned_url } = await fileSharingService.initiateUpload(
                { filename: `${ASSET_INVENTORY_FOLDER}/${selectedFile.name}`, file_size: selectedFile.size, checksum },
                abortController
            );
            await fileSharingService.uploadToS3(presigned_url, buffer, abortController.signal);
            await new Promise((resolve) => setTimeout(resolve, POST_UPLOAD_REFRESH_DELAY_MS));
            const { presigned_url: downloadUrl } = await fileSharingService.getDownloadUrl(uuid, abortController);
            await assetService.linkReportToAsset(
                props.assetUuid,
                uuid,
                downloadUrl,
                erasureStatus,
                toUtcDateString(erasureDate),
                abortController
            );
            if (!abortController.signal.aborted) {
                props.onUploadComplete();
                props.onClose();
            }
        } catch (error) {
            if (abortController.signal.aborted) {
                return;
            }
            logger.error("Failed to upload custom report", error instanceof Error ? error : new Error(String(error)));
            const message =
                extractServerMessage(error) ??
                (hasHttpStatus(error) && error.status === 400
                    ? t("FileSharing.upload.errorQuotaExceeded")
                    : t("FileSharing.upload.errorGeneric"));
            setErrorMessage(message);
            props.setUploading(false);
            setSelectedFile(undefined);
        }
    }

    function renderDropTargetContent() {
        if (selectedFile !== undefined) {
            return <span className={style.selectedFileName}>{selectedFile.name}</span>;
        }
        return (
            <Box verticalAlignment="Center" gap="Xxs">
                {errorMessage !== undefined ? (
                    <>
                        <ErrorIcon size={20} />
                        <span>{t("FileSharing.upload.errorGeneric")}</span>
                    </>
                ) : (
                    <>
                        <Upload color={resolveColor("--theme-primary")} />
                        <Box gap="Xxxs">
                            <span>{t("Settings.selectFile.dragDropMessage")}</span>
                            <span>{t("Common.lowerCaseOr")}</span>
                        </Box>
                    </>
                )}
                <LinkText as="button" className={style.browseLink} onClick={() => fileInputRef.current?.click()}>
                    {t("Settings.selectFile.browseFiles")}
                </LinkText>
            </Box>
        );
    }

    function renderContent() {
        return (
            <Box className={style.form}>
                <Box gap="Md" verticalAlignment="Top">
                    <span className={style.formLabel}>
                        {t("AssetManagement.sidePanel.erasureHistory.erasureStatus")}
                    </span>
                    <Box className={style.formField}>
                        <Select
                            name="erasureStatus"
                            placeholder=""
                            items={ERASURE_STATUS_ITEMS}
                            selectedByDefault={erasureStatus}
                            onSelection={(value: string) => setErasureStatus(value)}
                            hideLabelSection
                            closeOnSelection
                            key={openCount}
                            width={306}
                        />
                    </Box>
                </Box>
                <Box gap="Md" verticalAlignment="Top">
                    <span className={style.formLabel}>{t("AssetManagement.sidePanel.erasureHistory.date")}</span>
                    <Box className={style.formField}>
                        <div ref={datePickerAnchorRef}>
                            <DatePicker
                                caretAs={(colorProps) => <CalendarIcon {...colorProps} color={theme.iconFillColor} />}
                                value={erasureDate ?? null}
                                onChange={(date: Date | null) => {
                                    setErasureDate(date ? resolveErasureDate(date) : undefined);
                                    setDateError(undefined);
                                }}
                                format="dd/MM/yyyy"
                                placement="bottomStart"
                                placeholder={t("WorkflowsTable.filter.selectDate")}
                                container={getDatePickerContainer}
                                menuClassName="uploadReportDatePickerPopup"
                                name={"Date"}
                                error={dateError}
                                className={"uploadCustomReportDialogDatePicker"}
                            />
                        </div>
                    </Box>
                </Box>
                <Box gap="Md" verticalAlignment="Top">
                    <span className={style.formLabel}>
                        {t("AssetManagement.sidePanel.erasureHistory.uploadReport")}
                    </span>
                    <Box
                        columnLayout
                        gap="Xxs"
                        className={classNames(style.uploadDropTarget, {
                            [style.uploadDropTargetError]: errorMessage !== undefined,
                        })}
                    >
                        <Box columnLayout gap="Xxxs">
                            <FileDrop
                                onDrop={(fileList) => {
                                    if (fileList?.length) {
                                        handleFile(fileList[0]);
                                    }
                                }}
                            >
                                {renderDropTargetContent()}
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    multiple={false}
                                    className={style.hiddenInput}
                                    onChange={(e) => {
                                        if (e.target.files?.length) {
                                            handleFile(e.target.files[0]);
                                        }
                                    }}
                                />
                            </FileDrop>
                        </Box>
                    </Box>
                </Box>
                {errorMessage !== undefined && (
                    <ContextualBanner variant="Error" className={style.errorBanner}>
                        {errorMessage}
                    </ContextualBanner>
                )}
            </Box>
        );
    }

    return (
        <Modal
            className={style.dialog}
            isOpen={props.isOpen && !props.uploading}
            hideModal={props.onClose}
            width={"Lg"}
            title={t("AssetManagement.sidePanel.erasureHistory.uploadReportDialogTitle", {
                assetId: props.assetIdentifier,
            })}
            buttons={
                <>
                    <Button variant="Primary" onClick={() => void handleSubmit()} disabled={selectedFile === undefined}>
                        {t("Common.ok")}
                    </Button>
                    <Button variant="Secondary" onClick={props.onClose}>
                        {t("Common.cancel")}
                    </Button>
                </>
            }
        >
            {renderContent()}
        </Modal>
    );
}
